import { migrateSectIds } from './data/sectMigration';
import { parseAutoBudget, type AutoBudget } from './autoBudget';
import { QI_POOL } from './cultivation';
import { MARTIAL_ARTS, SKILLS, STARTER_ART } from './data/skills';
import { NEIGONG } from './data/neigong';
import { QUESTS } from './data/quests';
import { MAX_BADGE } from './data/badges';
import { SCHOOLS } from './data/schools';
import { BAG_SIZE, EQUIP_SLOTS, ITEMS, itemOf, migrateRoll, type EquipSlot, type ItemRoll } from './data/items';
import { FORGE_MAX, INFUSE_MAX, isForgeElement, type WeaponForge } from './data/forge';
import { MOUNTS, STARTER_MOUNTS } from './data/mounts';
import { PETS, STARTER_PET } from './data/pets';
import { HUB, isZoneId, START, type ZoneId } from './data/zones';
import { ownsSkin, savedSkinSlot, SKIN_SLOTS, SKINS, STARTER_SKINS, type SkinSlot } from './data/skins';
import { titleByName, TITLES } from './data/titles';
import { SEASON, type PassTrack } from './data/pass';
import { LADDER, LADDERS, type LadderId } from './data/ladder';
import { ARENA, ARENA_ROSTER } from './data/arena';
import { TRAIN_TICK, type TrainingState } from './training';
import { TOPUP_MILESTONES, TOPUP_PACKS, TOPUP_RECEIPTS_KEPT } from './data/topup';
import type { Karma } from './karma';
import { newTrades, parseTrades, type TradeState } from './professions';
import { FELLOWS, PARTY } from './data/party';
import { newGroupDance, parseGroupDance, type GroupDanceState } from './groupDance';
import { migrateQinggong } from './qinggong';
import { OPENING_SCENES, SCENES } from './data/scenes';
import { newCarriage, parseCarriage, type CarriageState } from './carriage';
import { newNpcMemory, parseNpcMemory, type NpcMemoryState } from './systems/npcMemory';
import { parseIndoors, type IndoorsState } from './interiors';
import { defaultAppearance, parseAppearance, type Appearance } from './data/appearance';
import { parseOffline, type OfflineState } from './offlineWork';
import { parseSparring, type SparringState } from './sparring';
import { parseBreakthrough, type BreakthroughState } from './data/breakthrough';
import { parseDungeonSave, type DungeonSave } from './data/dungeon';
import { parseQgPity } from './data/qinggongDrops';
import { parseWorldBossSave, type WorldBossSave } from './data/worldBosses';
import { sanitizeSkillBar } from './systems/skillBar';
import { sanitizeMovesLearned } from './systems/moveLocks';
import { isWalkMode, type WalkMode } from './data/walkModes';
import { parseRedLantern, type RedLanternSave } from './systems/redLantern';
import { parseSecrets, type SecretsSave } from './systems/secrets';
import { parseTrials, type TrialsSave } from './systems/trials';
import { parsePlainsStory, type PlainsStorySave } from './systems/plainsStory';
import { parseTourney, type TourneySave } from './systems/tournament';
import { parseWardrobe, type WardrobeSave } from './data/wardrobe';
import { parseSocial, type SocialSave } from './data/social';
import { parseWarehouse } from './systems/warehouse';

export type QuestStatus = 'available' | 'active' | 'ready';

export interface QuestState {
  /** Index into QUESTS; equal to QUESTS.length once the chain is finished. */
  index: number;
  status: QuestStatus;
  progress: number[];
}

export interface TopUpState {
  baht: number;
  packs: string[];
  receipts: string[];
  claimed: number[];
}

export interface ArenaState {
  /** The day this ranking belongs to (set by the client's calendar; '' before the first). */
  day: string;
  order: number[];
  attempts: number;
  wins: number;
  losses: number;
}

/** A fresh ranking: the roster in order, the player last. */
export const newArena = (day = ''): ArenaState => ({
  day,
  order: [...ARENA_ROSTER.keys(), -1],
  attempts: 0,
  wins: 0,
  losses: 0,
});

export interface LadderStanding {
  rating: number;
  wins: number;
  losses: number;
  /** Ladder points to spend in its shop. */
  points: number;
  /** Tiers whose reward was taken. */
  claimed: number[];
  /** The arena day `pointWins` counts for, and the point-paying wins that day (data/ladder.ts `dailyPointWins`). */
  day?: string;
  pointWins?: number;
}

const newStanding = (): LadderStanding => ({
  rating: LADDER.startRating,
  wins: 0,
  losses: 0,
  points: 0,
  claimed: [],
});

export interface PassState {
  season: string;
  xp: number;
  premium: boolean;
  /** Tier levels whose reward was taken, per track. */
  claimed: Record<PassTrack, number[]>;
}

export const newPass = (): PassState => ({
  season: SEASON.id,
  xp: 0,
  premium: false,
  claimed: { free: [], premium: [] },
});

/** Everything about the player that persists between sessions. */
export interface PlayerProgress {
  version: 1;
  name: string;
  /** How the character looks (the creator's choices; data/appearance.ts). */
  appearance: Appearance;
  title: string;
  gold: number;
  /** หยกเซียน: the item mall's currency. */
  jade: number;
  /** Paid top-ups: total baht, packs already bought once, recent receipt ids, milestones claimed. */
  topUp: TopUpState;
  /** This season's battle pass. */
  pass: PassState;
  /** The neigong or move being trained, or null. */
  training: TrainingState | null;
  /** When a level finishes, start the next one of the same thing. */
  trainAuto: boolean;
  /** The arena today: ranking (roster indices, -1 the player), challenges used, wins and losses. */
  arena: ArenaState;
  /** H16 small open dungeons: camp id -> game day of the last daily reward (systems/smallDungeons.ts). */
  smallDungeonDay?: Record<string, number>;
  /** J2 auto-play budget (autoBudget.ts). */
  autoBudget?: AutoBudget;
  /** L2 combat qi pool (systems/qiPool.ts): qi waiting to be refined by sitting. Absent = empty. */
  qiPool?: number;
  /** L7 codex (systems/codex.ts): kills per family and the tiers paid so far. Absent = none. */
  codex?: { n: Record<string, number>; tier: Record<string, number> };
  /** K31 fast travel (systems/fastTravel.ts): stations discovered and the warp cooldown left (s). Absent = none. */
  fastTravel?: { seen: string[]; cd: number };
  /** Standing on the heavenly and hellish ladders this season. */
  ladder: { season: string } & Record<LadderId, LadderStanding>;
  /** Unspent แต้มฝึกปรือ. Fractional; the UI floors it. */
  cultivation: number;
  cultivationSpent: number;
  arts: string[];
  activeArt: string;
  /**
   * The skill bar's eight slots (move ids, null for empty), once the player has arranged it; until then the bar is
   * the active art's moves (systems/skillBar.ts).
   */
  skillBar?: (string | null)[];
  /** The moves learned from a master so far; absent: every move of a learned art is usable (systems/moveLocks.ts). */
  movesLearned?: string[] | undefined;
  skillLevels: Record<string, number>;
  /** Learned neigong → level. */
  neigong: Record<string, number>;
  activeNeigong: string;
  /** Joined school id, or null for a wandering martial artist. */
  school: string | null;
  /** Item ids carried, including what is worn (duplicates allowed). */
  inventory: string[];
  /** Items with rolled stats, by their own id (`roll-N`), and the next number to use. */
  rolls: Record<string, ItemRoll>;
  nextRoll: number;
  /** Worn item per slot; each must also be in the inventory. */
  equipment: Partial<Record<EquipSlot, string>>;
  quest: QuestState;
  /** Mounts owned, and the one X summons. */
  mounts: string[];
  mountPick: string;
  /** Pets owned (the fox from the start) and the one at the heel. */
  pets: string[];
  petPick: string;
  /** Which zone the character is in, and whether they arrive there by its warp circle. */
  location: ZoneId;
  /** Arrived through a warp circle from this zone (set down beside the circle back), or null. */
  arriveFrom: ZoneId | null;
  /** Inside a building of that zone (which, which floor, where), or null outside (interiors.ts). */
  indoors: IndoorsState | null;
  /** Standing from killing other players, PK points, and jail time left (seconds). */
  karma: Karma;
  pkPoints: number;
  jailT: number;
  /** Titles earned (ids), the one shown over the head, and monsters defeated by kind. */
  titles: string[];
  titleWorn: string;
  kills: Record<string, number>;
  /** Skins owned, and the one worn in each slot. */
  skins: string[];
  skinWorn: Partial<Record<SkinSlot, string>>;
  /** School badges claimed: per school, the highest tier (see data/badges). */
  badges: Record<string, number>;
  /** Rank of school uniform worn (0 = the plain uniform; at most the badge claimed). */
  uniformTier: number;
  /** Fellow wanderers travelling with the player (party members besides the player, by id). */
  party: string[];
  /** วิชาตัวเบา learned → level (data/qinggong; none for a new character). */
  qinggong: Record<string, number>;
  /** Professions: trade xp, goods carried and planted plots (see professions.ts). */
  trades: TradeState;
  /** Group dance (รำกลุ่ม): paying sessions today and the buff left (see groupDance.ts). */
  groupDance: GroupDanceState;
  /** Carriages (รถม้า): stations reached, a ride's arrival under way, rides taken (see carriage.ts). */
  carriage: CarriageState;
  /** Cutscenes already played, watched or skipped (data/scenes): each plays once per character. */
  scenes: string[];
  /** What townsfolk remember of the player, per person and per village (systems/npcMemory.ts). */
  npcMemory: NpcMemoryState;
  /** Offline work and kidnapping (offlineWork.ts); absent until first used. */
  offline?: OfflineState;
  /** Sparring partners beaten and today's paid wins (sparring.ts); absent until the first win. */
  sparring?: SparringState;
  /** The breakthrough gate at stage 9 (data/breakthrough.ts); absent until the first hold. */
  breakthrough?: BreakthroughState;
  /** ถ้ำคลื่นคำราม: the day of the last boss-chest claim and clears (systems/dungeon.ts); absent until the first clear. */
  dungeon?: DungeonSave;
  /** บอสโลก: full chests claimed per boss on an arena day (systems/worldBosses.ts); absent until the first kill. */
  worldBoss?: WorldBossSave;
  /** Bad-luck counters of the qinggong drops: dry claims per source since the last pay (data/qinggongDrops.ts `pityKey`). */
  qgPity?: Record<string, number>;
  /** เดินช้า / เดินเร็ว (systems/walkMode.ts); absent: the default (fast). */
  walkMode?: WalkMode;
  /** Qinggong time trials: best times and medals paid (systems/trials.ts); absent until the first finish. */
  trials?: TrialsSave;
  /** Vistas seen and chests opened (systems/secrets.ts); absent until the first. */
  secrets?: SecretsSave;
  /** The red lantern's count, guard and open note (systems/redLantern.ts); absent until the first roll. */
  redLantern?: RedLanternSave;
  /** ตู้เสื้อผ้า: fashion and gear hidden, dyes, saved sets (systems/wardrobe.ts); absent until first changed. */
  wardrobe?: WardrobeSave;
  /** เพื่อน and คู่แค้น (systems/social.ts); absent until first used. */
  social?: SocialSave;
  /** The 256 m plains' story: the fair's choice when it is not a school (systems/plainsStory.ts); absent until made. */
  plainsStory?: PlainsStorySave;
  /** ประลองยุทธภพ: the weekly tournament's run, coins, hall and the palm's holder (systems/tournament.ts); absent until first used. */
  tourney?: TourneySave;
  /** Epoch ms of the last save; drives offline cultivation. */
  savedAt: number;
}

/** A saved forge kept within its limits (or dropped when it is not one). */
function repairForge(f: unknown): { forge?: WeaponForge } {
  if (!f || typeof f !== 'object') return {};
  const r = f as Partial<WeaponForge>;
  const int = (v: unknown, max: number) =>
    typeof v === 'number' && Number.isFinite(v) ? Math.min(max, Math.max(0, Math.floor(v))) : 0;
  const el = isForgeElement(r.el) ? r.el : null;
  return { forge: { plus: int(r.plus, FORGE_MAX), el, lv: el ? int(r.lv, INFUSE_MAX) : 0 } };
}

/** What every new character (and every save from before equipment existed) starts with. */
const STARTER_GEAR = ['iron-sword', 'navy-coat'];

/** The starter art's move that stands in for a hidden art's move of the same place (its 1st for the 1st, and so on). */
function hiddenMove(id: string): string | null {
  const art = Object.values(MARTIAL_ARTS).find((a) => a.hidden && a.skills.includes(id));
  if (!art) return null;
  return MARTIAL_ARTS[STARTER_ART]!.skills[art.skills.indexOf(id)] ?? null;
}

/** Move levels, with a hidden art's moves carried to the starter art's (the higher kept when both are there). */
function movedLevels(levels: Record<string, number>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [id, lv] of Object.entries(levels)) {
    const to = hiddenMove(id) ?? id;
    out[to] = Math.max(out[to] ?? 0, lv);
  }
  return out;
}

export function newProgress(now = Date.now()): PlayerProgress {
  return {
    version: 1,
    name: 'เทพกระบี่มาร',
    appearance: defaultAppearance(),
    title: 'ผู้ลงนามสัญญาเลือด',
    gold: 0,
    jade: 200,
    topUp: { baht: 0, packs: [], receipts: [], claimed: [] },
    pass: newPass(),
    training: null,
    trainAuto: false,
    ladder: { season: LADDER.season, heaven: newStanding(), hell: newStanding() },
    arena: newArena(),
    cultivation: 0,
    cultivationSpent: 0,
    arts: [STARTER_ART],
    activeArt: STARTER_ART,
    skillLevels: {},
    neigong: { 'blood-oath': 1 },
    activeNeigong: 'blood-oath',
    school: null,
    inventory: [...STARTER_GEAR],
    rolls: {},
    nextRoll: 1,
    equipment: { weapon: 'iron-sword', body: 'navy-coat' },
    quest: { index: 0, status: 'available', progress: [] },
    mounts: [...STARTER_MOUNTS],
    mountPick: STARTER_MOUNTS[0]!,
    pets: [STARTER_PET],
    petPick: STARTER_PET,
    location: START,
    arriveFrom: null,
    indoors: null,
    karma: 'normal',
    pkPoints: 0,
    jailT: 0,
    titles: ['blood-oath'],
    titleWorn: 'blood-oath',
    kills: {},
    skins: [...STARTER_SKINS],
    skinWorn: {},
    badges: {},
    uniformTier: 0,
    party: [],
    qinggong: {},
    trades: newTrades(),
    groupDance: newGroupDance(),
    carriage: newCarriage(),
    scenes: [],
    npcMemory: newNpcMemory(),
    savedAt: now,
  };
}

export function serializeProgress(p: PlayerProgress, now = Date.now()): string {
  return JSON.stringify({ ...p, savedAt: now });
}

const isRecordOfNumbers = (v: unknown): v is Record<string, number> =>
  !!v && typeof v === 'object' && Object.values(v).every((x) => typeof x === 'number' && Number.isFinite(x));

/**
 * The mounts a save keeps: the starter mounts, every known mount it owned (saves from before mounts
 * have none; unknown ones are dropped), and the mounts its titles grant. A mount bound to a title
 * stays only while that title is held, and comes back with it (saves from before the black eagle).
 */
/** The pets a save keeps: the starter and every known pet it owned; the pick must be owned. */
function ownedPets(raw: unknown, pick: unknown): { pets: string[]; petPick: string } {
  const owned = Array.isArray(raw) ? raw.filter((p): p is string => typeof p === 'string' && !!PETS[p]) : [];
  const pets = Array.from(new Set([STARTER_PET, ...owned]));
  return { pets, petPick: typeof pick === 'string' && pets.includes(pick) ? pick : STARTER_PET };
}

function ownedMounts(raw: unknown, titles: readonly string[]): string[] {
  const owned = Array.isArray(raw)
    ? raw.filter((m): m is string => typeof m === 'string' && !!MOUNTS[m])
    : [];
  const granted = titles.flatMap((t) => TITLES[t]?.grants?.mount ?? []).filter((m) => !!MOUNTS[m]);
  const held = (m: string) => {
    const t = MOUNTS[m]?.title;
    return !t || titles.includes(t);
  };
  return Array.from(new Set([...STARTER_MOUNTS, ...owned.filter(held), ...granted]));
}

/**
 * Parses a save, dropping unknown ids and repairing anything invalid against current data.
 * Returns null when the input is not a save at all.
 */
export function parseProgress(json: string): PlayerProgress | null {
  let raw: unknown;
  try {
    raw = migrateSectIds(JSON.parse(json));
  } catch {
    return null;
  }
  if (!raw || typeof raw !== 'object' || (raw as { version?: unknown }).version !== 1) return null;
  const r = raw as Partial<PlayerProgress>;
  const base = newProgress(0);
  const num = (v: unknown, d: number) => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : d);

  const arts = Array.isArray(r.arts)
    ? r.arts.filter((a): a is string => typeof a === 'string' && !!MARTIAL_ARTS[a] && !MARTIAL_ARTS[a]!.hidden)
    : [];
  // The legendary nine swords is not for players (data/skills.ts `hidden`): a save that held it gets the starter art instead.
  const hadHidden = Array.isArray(r.arts) && r.arts.some((a) => typeof a === 'string' && MARTIAL_ARTS[a]?.hidden);
  if (hadHidden || !arts.length) {
    if (!arts.includes(STARTER_ART)) arts.unshift(STARTER_ART);
  }
  const neigong: Record<string, number> = {};
  if (isRecordOfNumbers(r.neigong))
    for (const [id, lv] of Object.entries(r.neigong))
      if (NEIGONG[id]) neigong[id] = Math.min(NEIGONG[id].maxLevel, Math.max(1, Math.floor(lv)));
  if (!neigong['blood-oath']) neigong['blood-oath'] = 1;

  // Titles: known ids kept; saves from before keep the title they showed (by its name).
  const titles = (() => {
    const titles = Array.isArray(r.titles)
      ? r.titles.filter((t): t is string => typeof t === 'string' && !!TITLES[t])
      : [];
    if (!titles.includes('blood-oath')) titles.unshift('blood-oath');
    const shown = typeof r.title === 'string' ? titleByName(r.title) : undefined;
    if (shown && !titles.includes(shown.id)) titles.push(shown.id);
    const titleWorn =
      typeof r.titleWorn === 'string' && titles.includes(r.titleWorn)
        ? r.titleWorn
        : (shown?.id ?? 'blood-oath');
    return { titles, titleWorn, kills: isRecordOfNumbers(r.kills) ? { ...r.kills } : {} };
  })();

  const q = r.quest;
  const quest: QuestState =
    q && typeof q === 'object' && typeof q.index === 'number' && q.index >= 0 && q.index <= QUESTS.length
      ? {
          index: Math.floor(q.index),
          status: q.status === 'active' || q.status === 'ready' ? q.status : 'available',
          progress: Array.isArray(q.progress) ? q.progress.map((n) => num(n, 0)) : [],
        }
      : base.quest;

  // Saves from before equipment get the starter gear; unknown items are dropped.
  // Rolled items whose base still exists.
  const rolls: Record<string, ItemRoll> = {};
  if (r.rolls && typeof r.rolls === 'object')
    for (const [id, roll] of Object.entries(r.rolls as Record<string, ItemRoll>))
      if (
        roll &&
        typeof roll === 'object' &&
        ITEMS[roll.base] &&
        roll.stats &&
        typeof roll.stats === 'object'
      ) {
        // Items v2: pre-v2 rolls become affixes with equal stats (data/items.ts `migrateRoll`).
        const m = migrateRoll({ ...roll, ...repairForge(roll.forge) });
        if (m) rolls[id] = m;
      }
  const inventory = Array.isArray(r.inventory)
    ? r.inventory.filter((i): i is string => typeof i === 'string' && !!itemOf(rolls, i)).slice(0, BAG_SIZE)
    : [...STARTER_GEAR];
  const equipment: Partial<Record<EquipSlot, string>> = {};
  const worn =
    r.equipment && typeof r.equipment === 'object'
      ? r.equipment
      : Array.isArray(r.inventory)
        ? {}
        : base.equipment;
  for (const slot of EQUIP_SLOTS) {
    const id = (worn as Record<string, unknown>)[slot];
    if (typeof id === 'string' && itemOf(rolls, id)?.slot === slot && inventory.includes(id))
      equipment[slot] = id;
  }

  return {
    version: 1,
    name: typeof r.name === 'string' && r.name ? r.name : base.name,
    // Saves from before the creator keep the look they had: Model 0, the man, as built.
    appearance: parseAppearance(r.appearance),
    title: typeof r.title === 'string' && r.title ? r.title : base.title,
    gold: num(r.gold, 0),
    jade: num(r.jade, 200),
    trainAuto: r.trainAuto === true,
    autoBudget: parseAutoBudget(r.autoBudget),
    qiPool:
      typeof r.qiPool === 'number' && Number.isFinite(r.qiPool) && r.qiPool > 0 ? Math.min(QI_POOL.cap, r.qiPool) : undefined,
    codex: parseCodex(r.codex),
    fastTravel: parseFastTravel(r.fastTravel),
    smallDungeonDay:
      r.smallDungeonDay && typeof r.smallDungeonDay === 'object'
        ? Object.fromEntries(
            Object.entries(r.smallDungeonDay as Record<string, unknown>).filter(([, v]) => typeof v === 'number'),
          ) as Record<string, number>
        : undefined,
    arena: (() => {
      const a = r.arena && typeof r.arena === 'object' ? (r.arena as unknown as Record<string, unknown>) : {};
      const day = typeof a.day === 'string' ? a.day : '';
      const order = Array.isArray(a.order) ? a.order : [];
      // The ranking must hold every contender and the player exactly once.
      const ok =
        order.length === ARENA_ROSTER.length + 1 &&
        new Set(order).size === order.length &&
        order.every((n) => Number.isInteger(n) && n >= -1 && n < ARENA_ROSTER.length);
      if (!ok) return newArena(day);
      return {
        day,
        order: order as number[],
        attempts: Math.min(num(a.attempts, 0), ARENA.attemptsPerDay),
        wins: num(a.wins, 0),
        losses: num(a.losses, 0),
      };
    })(),
    // A new ladder season starts everyone over.
    ladder: (() => {
      const l =
        r.ladder && typeof r.ladder === 'object' ? (r.ladder as unknown as Record<string, unknown>) : {};
      const fresh = l.season !== LADDER.season;
      const one = (id: LadderId): LadderStanding => {
        const s = !fresh && l[id] && typeof l[id] === 'object' ? (l[id] as Record<string, unknown>) : null;
        if (!s) return newStanding();
        return {
          rating: num(s.rating, LADDER.startRating),
          wins: num(s.wins, 0),
          losses: num(s.losses, 0),
          points: num(s.points, 0),
          ...(typeof s.day === 'string' ? { day: s.day, pointWins: num(s.pointWins, 0) } : {}),
          claimed: Array.isArray(s.claimed)
            ? s.claimed.filter(
                (n): n is number => Number.isInteger(n) && n >= 0 && n < LADDERS[id].tiers.length,
              )
            : [],
        };
      };
      return { season: LADDER.season, heaven: one('heaven'), hell: one('hell') };
    })(),
    training: (() => {
      const t =
        r.training && typeof r.training === 'object'
          ? (r.training as unknown as Record<string, unknown>)
          : null;
      if (!t || (t.kind !== 'skill' && t.kind !== 'neigong') || typeof t.id !== 'string') return null;
      const tid: string = t.kind === 'skill' ? (hiddenMove(t.id) ?? t.id) : t.id;
      if (t.kind === 'neigong' ? !NEIGONG[tid] : !SKILLS[tid]) return null;
      const level = num(t.level, 0);
      if (!Number.isInteger(level) || level < 2) return null;
      const total = num(t.total, 0);
      if (!total) return null;
      const cost = num(t.cost, 0);
      return {
        kind: t.kind,
        id: tid,
        level,
        left: Math.min(num(t.left, 0), total),
        total,
        cost,
        paid: Math.min(num(t.paid, 0), cost),
        tick: Math.min(num(t.tick, 0), TRAIN_TICK),
      };
    })(),
    // A pass from another season starts over.
    pass: (() => {
      const t = r.pass && typeof r.pass === 'object' ? (r.pass as unknown as Record<string, unknown>) : {};
      if (t.season !== SEASON.id) return newPass();
      const c = t.claimed && typeof t.claimed === 'object' ? (t.claimed as Record<string, unknown>) : {};
      const levels = (v: unknown) =>
        Array.isArray(v)
          ? v.filter((n): n is number => Number.isInteger(n) && n >= 1 && n <= SEASON.maxLevel)
          : [];
      return {
        season: SEASON.id,
        xp: num(t.xp, 0),
        premium: t.premium === true,
        claimed: { free: levels(c.free), premium: levels(c.premium) },
      };
    })(),
    topUp: (() => {
      const t = r.topUp && typeof r.topUp === 'object' ? (r.topUp as unknown as Record<string, unknown>) : {};
      const strs = (v: unknown) =>
        Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
      return {
        baht: num(t.baht, 0),
        packs: strs(t.packs).filter((id) => TOPUP_PACKS.some((k) => k.id === id)),
        receipts: strs(t.receipts).slice(-TOPUP_RECEIPTS_KEPT),
        claimed: Array.isArray(t.claimed)
          ? t.claimed.filter((b): b is number => TOPUP_MILESTONES.some((m) => m.baht === b))
          : [],
      };
    })(),
    cultivation: num(r.cultivation, 0),
    cultivationSpent: num(r.cultivationSpent, 0),
    arts,
    activeArt: typeof r.activeArt === 'string' && arts.includes(r.activeArt) ? r.activeArt : arts[0]!,
    ...(() => {
      const bar = sanitizeSkillBar(r.skillBar);
      return bar ? { skillBar: bar.map((id) => (id ? (hiddenMove(id) ?? id) : id)) } : {};
    })(),
    ...(() => {
      const learned = sanitizeMovesLearned(r.movesLearned);
      return learned ? { movesLearned: [...new Set(learned.map((id) => hiddenMove(id) ?? id))] } : {};
    })(),
    skillLevels: movedLevels(isRecordOfNumbers(r.skillLevels) ? r.skillLevels : {}),
    neigong,
    activeNeigong:
      typeof r.activeNeigong === 'string' && neigong[r.activeNeigong] ? r.activeNeigong : 'blood-oath',
    school: typeof r.school === 'string' && SCHOOLS[r.school] ? r.school : null,
    inventory,
    rolls,
    nextRoll: Math.max(num(r.nextRoll, 1), Object.keys(rolls).length + 1),
    equipment,
    quest,
    mounts: ownedMounts(r.mounts, titles.titles),
    ...ownedPets(r.pets, r.petPick),
    ...titles,
    ...(() => {
      const skins = Array.from(
        new Set([
          ...STARTER_SKINS,
          ...(Array.isArray(r.skins)
            ? r.skins.filter((s): s is string => typeof s === 'string' && !!SKINS[s])
            : []),
        ]),
      );
      const skinWorn: Partial<Record<SkinSlot, string>> = {};
      const worn =
        r.skinWorn && typeof r.skinWorn === 'object' ? (r.skinWorn as Record<string, unknown>) : {};
      for (const slot of SKIN_SLOTS) {
        const id = worn[slot];
        const to = typeof id === 'string' ? savedSkinSlot(slot, id) : null;
        if (to && !skinWorn[to] && ownsSkin(skins, id as string)) skinWorn[to] = id as string;
      }
      return { skins, skinWorn };
    })(),
    ...(() => {
      // Badges: known schools, tiers in range; the uniform worn no higher than the school's badge.
      const badges: Record<string, number> = {};
      if (isRecordOfNumbers(r.badges))
        for (const [id, t] of Object.entries(r.badges))
          if (SCHOOLS[id] && t >= 1) badges[id] = Math.min(MAX_BADGE, Math.floor(t));
      const school = typeof r.school === 'string' ? r.school : '';
      const tier = typeof r.uniformTier === 'number' ? Math.floor(r.uniformTier) : 0;
      return { badges, uniformTier: Math.max(0, Math.min(badges[school] ?? 0, tier)) };
    })(),
    karma: r.karma === 'good' || r.karma === 'evil' || r.karma === 'mad' ? r.karma : 'normal',
    pkPoints: num(r.pkPoints, 0),
    location: isZoneId(r.location) ? r.location : HUB,
    // Saves from before zones kept only whether the player came by the (single) warp circle.
    arriveFrom: isZoneId(r.arriveFrom)
      ? r.arriveFrom
      : (r as { arriveByPortal?: unknown }).arriveByPortal === true
        ? r.location === 'snow'
          ? HUB
          : 'snow'
        : null,
    // Saves from before interiors are outside; a place indoors that no longer exists is too.
    indoors: parseIndoors(r.indoors, isZoneId(r.location) ? r.location : HUB),
    jailT: num(r.jailT, 0),
    party: Array.isArray(r.party)
      ? [...new Set(r.party.filter((f): f is string => typeof f === 'string' && !!FELLOWS[f]))].slice(
          0,
          PARTY.maxSize - 1,
        )
      : [],
    mountPick: typeof r.mountPick === 'string' && MOUNTS[r.mountPick] ? r.mountPick : STARTER_MOUNTS[0]!,
    // Saves from before qinggong was learned keep every way of moving they had.
    qinggong: migrateQinggong(r.qinggong),
    // Saves from before professions start with no trades, goods or plots.
    trades: parseTrades(r.trades),
    // Saves from before the group dance start with no sessions danced and no buff.
    groupDance: parseGroupDance(r.groupDance),
    // Saves from before carriages unlock the stations already reached on foot (carriage.ts).
    carriage: parseCarriage(r.carriage, {
      location: isZoneId(r.location) ? r.location : HUB,
      school: typeof r.school === 'string' && SCHOOLS[r.school] ? r.school : null,
    }),
    // Saves from before cutscenes never see the opening: it counts as seen.
    scenes: Array.isArray(r.scenes)
      ? [...new Set(r.scenes.filter((s): s is string => typeof s === 'string' && !!SCENES[s]))]
      : [...OPENING_SCENES],
    // Saves from before NPC memory: nobody remembers anything yet.
    npcMemory: parseNpcMemory(r.npcMemory),
    ...(r.offline ? { offline: parseOffline(r.offline) } : {}),
    ...(r.sparring ? { sparring: parseSparring(r.sparring) } : {}),
    ...(r.breakthrough ? { breakthrough: parseBreakthrough(r.breakthrough) } : {}),
    ...(r.dungeon ? { dungeon: parseDungeonSave(r.dungeon) } : {}),
    ...(r.worldBoss ? { worldBoss: parseWorldBossSave(r.worldBoss) } : {}),
    ...(parseQgPity(r.qgPity) ? { qgPity: parseQgPity(r.qgPity)! } : {}),
    ...(isWalkMode(r.walkMode) ? { walkMode: r.walkMode } : {}),
    ...(r.trials ? { trials: parseTrials(r.trials) } : {}),
    ...(r.secrets ? { secrets: parseSecrets(r.secrets) } : {}),
    ...(r.redLantern ? { redLantern: parseRedLantern(r.redLantern) } : {}),
    ...(r.plainsStory ? { plainsStory: parsePlainsStory(r.plainsStory) ?? {} } : {}),
    ...(r.tourney ? { tourney: parseTourney(r.tourney) } : {}),
    ...(r.wardrobe ? { wardrobe: parseWardrobe(r.wardrobe) } : {}),
    ...(r.social ? { social: parseSocial(r.social) } : {}),
    ...(r.warehouse ? { warehouse: parseWarehouse(r.warehouse, rolls) } : {}),
    ...(r.guild && typeof r.guild === 'object' && Array.isArray(r.guild.members) ? { guild: r.guild } : {}),
    savedAt: num(r.savedAt, 0),
  };
}

function parseCodex(raw: unknown): { n: Record<string, number>; tier: Record<string, number> } | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  const pick = (v: unknown): Record<string, number> => {
    const out: Record<string, number> = {};
    if (v && typeof v === 'object')
      for (const [k, x] of Object.entries(v)) if (typeof x === 'number' && Number.isFinite(x) && x > 0) out[k] = Math.floor(x);
    return out;
  };
  const n = pick(o.n);
  return Object.keys(n).length ? { n, tier: pick(o.tier) } : undefined;
}

function parseFastTravel(raw: unknown): { seen: string[]; cd: number } | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  const seen = Array.isArray(o.seen) ? o.seen.filter((x): x is string => typeof x === 'string') : [];
  const cd = typeof o.cd === 'number' && Number.isFinite(o.cd) && o.cd > 0 ? o.cd : 0;
  return seen.length ? { seen, cd } : undefined;
}
